/**
 * Deutschlandatlas API client
 *
 * Note: GV100-Editionen fur die Zieleditions-Auswahl kommen NICHT von
 * diesem Router (er hat keinen /editions-Endpunkt), sondern vom
 * gemeinsamen `/admin/editions`-Endpunkt (siehe hooks/queries.ts -> useEditions).
 */

import axios from 'axios'
import { attachAuth } from '../../lib/apiAuth'

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8899'

const api = axios.create({
  baseURL: `${API_BASE}/admin/deutschlandatlas`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Types
// ============================================================================

export interface DaFilesResponse {
  data_dir: string
  files: string[]
}

export interface DaUploadResponse {
  filename: string
}

export interface IndicatorDescriptor {
  sheet: string
  kuerzel: string
  code: string
  name: string
  unit: string | null
  data_year: number | null
  level: 'county' | 'municipality' | 'municipal_association'
  value_kind: 'class' | 'numeric'
  n_values: number
}

export interface DaScanResponse {
  file: string
  indicators: IndicatorDescriptor[]
}

export interface DaSelection {
  sheet: string
  kuerzel: string
}

export interface DaExecuteRequest {
  file: string
  selections: DaSelection[]
  target_edition_id: number
}

export interface DaExecuteResponse {
  job_id: string
  celery_task_id: string
}

export interface DaImportResult {
  success: boolean
  created: number
  updated: number
  skipped: number
  errors: string[]
  data_source_id: number | null
  created_new_data_source: boolean
}

export interface DaImportStatus {
  job_id: string
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'rolled_back'
  result: DaImportResult | null
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * List available XLSX files in the Deutschlandatlas data directory
 */
export async function listFiles(): Promise<DaFilesResponse> {
  const response = await api.get<DaFilesResponse>('/files')
  return response.data
}

/**
 * Upload a new XLSX file into the Deutschlandatlas data directory
 */
export async function uploadXlsx(file: File): Promise<DaUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await api.post<DaUploadResponse>('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}

/**
 * Scan an XLSX file and list the indicators found in it
 */
export async function scanFile(file: string): Promise<DaScanResponse> {
  const response = await api.get<DaScanResponse>('/scan', {
    params: { file },
  })
  return response.data
}

/**
 * Execute Deutschlandatlas import
 */
export async function executeImport(request: DaExecuteRequest): Promise<DaExecuteResponse> {
  const response = await api.post<DaExecuteResponse>('/import/execute', request)
  return response.data
}

/**
 * Get import job status
 */
export async function getStatus(jobId: string): Promise<DaImportStatus> {
  const response = await api.get<DaImportStatus>(`/import/status/${jobId}`)
  return response.data
}

// Basic-Auth-Header + 401-Behandlung (optionaler Schutz, v0.3.8)
attachAuth(api)
